import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import type {
  BackgroundItem,
  BackgroundSettingsData,
} from "./backgrounds.interface";

@Injectable({
  providedIn: "root",
})
export class BackgroundService {
  constructor(private http: HttpClient) {}

  private buildParams(
    mine?: boolean,
    previewEmail?: string | null,
  ): HttpParams | undefined {
    let params: HttpParams | undefined;
    if (mine) {
      params = new HttpParams().set("mine", "true");
    }
    const email = (previewEmail ?? "").trim();
    if (email) {
      const base = params ?? new HttpParams();
      params = base.set("email", email);
    }
    return params;
  }

  getAll(mine?: boolean): Observable<{ backgrounds: BackgroundItem[] }> {
    return this.http.get<{ backgrounds: BackgroundItem[] }>("/backgrounds", {
      params: this.buildParams(mine),
    });
  }

  getOne(backgroundId: string, mine?: boolean): Observable<BackgroundItem> {
    return this.http.get<BackgroundItem>(
      `/backgrounds/${encodeURIComponent(backgroundId)}`,
      { params: this.buildParams(mine) },
    );
  }

  create(payload: {
    name: string;
    digitalCardProfileId?: string | null;
    settings: BackgroundSettingsData;
  }): Observable<BackgroundItem> {
    return this.http.post<BackgroundItem>("/backgrounds", payload);
  }

  update(
    backgroundId: string,
    payload: {
      name: string;
      digitalCardProfileId?: string | null;
      settings: BackgroundSettingsData;
    },
  ): Observable<BackgroundItem> {
    return this.http.put<BackgroundItem>(
      `/backgrounds/${encodeURIComponent(backgroundId)}`,
      payload,
    );
  }

  remove(backgroundId: string): Observable<void> {
    return this.http.delete<void>(
      `/backgrounds/${encodeURIComponent(backgroundId)}`,
    );
  }

  uploadImage(file: File): Observable<{ bannerImageUrl: string }> {
    const form = new FormData();
    form.append("image", file);
    return this.http.post<{ bannerImageUrl: string }>(
      "/backgrounds/upload",
      form,
    );
  }

  downloadImage(
    backgroundId: string,
    mine?: boolean,
    previewEmail?: string | null,
  ): Observable<Blob> {
    return this.http.get(
      `/backgrounds/${encodeURIComponent(backgroundId)}/image`,
      {
        params: this.buildParams(mine, previewEmail),
        responseType: "blob",
      },
    );
  }

  downloadZip(backgroundId: string, mine?: boolean): Observable<Blob> {
    return this.http.get(
      `/backgrounds/${encodeURIComponent(backgroundId)}/zip`,
      {
        params: this.buildParams(mine),
        responseType: "blob",
      },
    );
  }

  getPreviewQrTarget(
    digitalCardProfileId?: string | null,
    mine?: boolean,
    previewEmail?: string | null,
  ): Observable<{ qrTarget: string }> {
    let params = this.buildParams(mine, previewEmail) ?? new HttpParams();
    const profileId = (digitalCardProfileId ?? "").trim();
    if (profileId) {
      params = params.set("digitalCardProfileId", profileId);
    }
    return this.http.get<{ qrTarget: string }>("/backgrounds/qr-target", {
      params,
    });
  }

  getImagePreviewUrl(backgroundId: string, mine?: boolean): string {
    if (!mine) {
      return `/backgrounds/${encodeURIComponent(backgroundId)}/image`;
    }
    return `/backgrounds/${encodeURIComponent(backgroundId)}/image?mine=true`;
  }
}
